import { sentryMask } from 'sentryReplayPrivacy';
import { type ReimbursementTransaction } from 'API';
import { captureException } from '@sentry/react';
import { Box, CircularProgress, Typography, IconButton, Button } from '@mui/material';
import { ReactComponent as BackArrow } from 'assets/BackArrow.svg';
import { fileAccessService } from 'app/services/fileAccess.service';
import { useCodingSystemConfig } from 'app/hooks/transactions/useCodingSystemConfig';
import { useCodingDisplayValues } from 'app/hooks/transactions/useCodingDisplayValues';
import { useIsEnabled } from 'app/shared-components/feature-flags/FeatureFlagProvider';
import {
  REIMBURSEMENT_CODING_TYPE_COLUMNS,
  resolveReimbursementCodingType,
} from 'app/pages/overview/tabs/reimbursements/codingTypes';
import { MobileCodingFieldList } from 'app/pages/mobile/components/MobileCodingFieldList';
import { useAppSelector } from 'app/store';
import { selectUserCompanies } from 'app/store/userCompaniesSlice';
import MobilePageContent from 'app/pages/mobile/mobile-layout/MobilePageContent';
import { BACKGROUND_SIZES } from 'app/pages/mobile/utils/constants';
import { formatCurrency } from 'app/shared-components/util/Currency';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import MobileReceiptPreview from 'app/pages/mobile/pages/mobile-transactions/MobileReceiptPreview';
import TransactionField from 'app/pages/mobile/pages/mobile-transactions/TransactionField';
import { getReimbursementTransaction } from 'app/services/reimbursementService';
import {
  receiptItemsRadioOptions,
  formatTransactionType,
} from 'app/pages/overview/tabs/reimbursements/utils';
import { format } from 'date-fns';

interface MobileReimbursementDetailsProps {
  open: boolean;
  onClose: () => void;
  reimbursementId?: string;
}

const MobileReimbursementDetails = ({ open, onClose, reimbursementId }: MobileReimbursementDetailsProps) => {
  const { t } = useTranslation();
  const { selectedPaidolId } = useAppSelector(selectUserCompanies);
  const { isVisible, visibleColumnIds, getLabel, codingSystemType } = useCodingSystemConfig();
  const showCodingSection = visibleColumnIds.length > 0;
  const { 'equipment.enableEquipmentCoding': isEquipmentCodingEnabled } = useIsEnabled([
    { featureName: 'equipment', flagName: 'enableEquipmentCoding' },
  ]);
  const [reimbursement, setReimbursement] = useState<ReimbursementTransaction | null>(null);
  const [loading, setLoading] = useState(false);
  const [receiptUrl, setReceiptUrl] = useState<string | null>(null);
  const [receiptError, setReceiptError] = useState<Error | null>(null);

  // Fetch reimbursement transaction
  useEffect(() => {
    if (open && reimbursementId) {
      setLoading(true);
      setReimbursement(null);
      setReceiptUrl(null);
      setReceiptError(null);

      getReimbursementTransaction(reimbursementId)
        .then((data) => {
          if (data) {
            setReimbursement(data);
          }
        })
        .catch((error) => {
          captureException(error);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [open, reimbursementId]);

  // Load receipt URL
  useEffect(() => {
    const loadReceiptUrl = async () => {
      if (!reimbursement?.receipt?.document) {
        setReceiptUrl(null);
        setReceiptError(null);
        return;
      }

      try {
        setReceiptError(null);
        const url = await fileAccessService.getDownloadUrl(reimbursement.receipt.document);
        setReceiptUrl(url);
      } catch (error) {
        captureException(error);
        setReceiptError(error instanceof Error ? error : new Error('Failed to load receipt'));
        setReceiptUrl(null);
      }
    };

    if (open && reimbursement) {
      loadReceiptUrl();
    }
  }, [reimbursement, open]);

  // Get display values
  const getReceiptLineItemsDisplay = () => {
    if (!reimbursement?.receiptLineItems) return '--';
    const option = receiptItemsRadioOptions.find((opt) => opt.value === reimbursement.receiptLineItems);
    return option ? t(option.label) : '--';
  };

  const getTransactionMethodDisplay = () => {
    if (!reimbursement?.transactionType) return '--';
    return t(formatTransactionType(reimbursement.transactionType));
  };

  const codingDisplayValues = useCodingDisplayValues(reimbursement as Record<string, unknown> | null);

  // SEE-1148 — when equipment coding is on (Agave), show only the persisted
  // coding type's fields (GL Code + Vendor are common to every type), mirroring
  // the web reimbursement sheet. Flag off / Flex → the flat visible list.
  const readonlyColumnIds = useMemo(() => {
    if (!isEquipmentCodingEnabled || codingSystemType !== 'agave') return visibleColumnIds;
    const displayCodingType = resolveReimbursementCodingType(
      reimbursement?.codingType,
      reimbursement as { agaveEquipment?: unknown; agaveProject?: unknown } | null
    );
    return REIMBURSEMENT_CODING_TYPE_COLUMNS[displayCodingType].filter((id) => visibleColumnIds.includes(id));
  }, [isEquipmentCodingEnabled, codingSystemType, reimbursement, visibleColumnIds]);

  if (!open) return null;

  // Review step styles (matching edit transaction)
  const reviewSectionTitleStyle = {
    color: 'common.white',
    fontWeight: 'bold',
    mb: 1,
  };

  // Frosted "liquid glass" card — matches the submit review page.
  const reviewContainerStyle = {
    bgcolor: 'rgba(255, 255, 255, 0.12)',
    backdropFilter: 'blur(16px)',
    WebkitBackdropFilter: 'blur(16px)',
    border: '1px solid rgba(255, 255, 255, 0.25)',
    boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.3), 0 10px 32px rgba(0, 0, 0, 0.4)',
    borderRadius: 2,
    p: 2,
    display: 'flex',
    flexDirection: 'column',
  };

  const reviewFieldStyle = {
    '&:not(:last-child)': {
      borderBottom: '1px solid rgba(255, 255, 255, 0.18)',
      pb: 1.5,
      mb: 1.5,
    },
  };

  if (loading) {
    return (
      <MobilePageContent title="" transparentHeight={BACKGROUND_SIZES.FULL}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
          }}
        >
          <CircularProgress />
        </Box>
      </MobilePageContent>
    );
  }

  if (!reimbursement) {
    return (
      <MobilePageContent title="" transparentHeight={BACKGROUND_SIZES.FULL}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            gap: 2,
            px: 2,
          }}
        >
          <Typography color="error" variant="body1" sx={{ textAlign: 'center' }}>
            {t('reimbursementPage.errorLoadingDetails', 'Failed to load reimbursement details')}
          </Typography>
          <Button onClick={onClose} variant="outlined">
            {t('common.back', 'Back')}
          </Button>
        </Box>
      </MobilePageContent>
    );
  }

  // Mileage fields live on the CDK record but aren't declared on the Amplify type.
  const mileage = reimbursement as ReimbursementTransaction & {
    milesDriven?: number | null;
    purposeOfTrip?: string | null;
  };

  return (
    <MobilePageContent
      title=""
      transparentHeight={BACKGROUND_SIZES.FULL}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
      }}
    >
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 5, mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton
              onClick={onClose}
              aria-label="Back"
              sx={{
                color: 'white',
                padding: 0,
                '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.1)' },
              }}
            >
              <BackArrow style={{ height: '16px', width: 'auto' }} />
            </IconButton>
            <Typography variant="subtitle1" sx={{ color: 'common.white', fontWeight: 'bold' }}>
              {t('reimbursementPage.submitForm.reimbursementDetails')}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 5 }}>
          {/* Transaction info */}
          <Box>
            <Typography variant="subtitle1" sx={reviewSectionTitleStyle}>
              {t('reimbursementPage.sheet.transactionInfo')}
            </Typography>
            <Box sx={reviewContainerStyle}>
              <TransactionField
                label={t('reimbursementPage.sheet.amount')}
                value={
                  reimbursement.amount?.value ? formatCurrency({ value: reimbursement.amount.value }) : '--'
                }
                sx={reviewFieldStyle}
                invertColor
              />
              <TransactionField
                label={t('reimbursementPage.sheet.thisReflects')}
                value={getReceiptLineItemsDisplay()}
                sx={reviewFieldStyle}
                invertColor
              />
              <TransactionField
                label={t('reimbursementPage.sheet.paymentMethod')}
                value={getTransactionMethodDisplay()}
                sx={reviewFieldStyle}
                invertColor
              />
              <TransactionField
                label={t('reimbursementPage.sheet.merchant')}
                value={reimbursement.merchantName || '--'}
                sx={reviewFieldStyle}
                invertColor
              />
              <TransactionField
                label={t('reimbursementPage.sheet.transactionDate')}
                value={
                  reimbursement.transactionDate
                    ? format(new Date(reimbursement.transactionDate), 'MM/dd/yyyy')
                    : '--'
                }
                sx={reviewFieldStyle}
                invertColor
              />
              {mileage.milesDriven != null && (
                <>
                  <TransactionField
                    label={t('reimbursementPage.sheet.milesDriven')}
                    value={String(mileage.milesDriven)}
                    sx={reviewFieldStyle}
                    invertColor
                  />
                  <TransactionField
                    label={t('reimbursementPage.sheet.purposeOfTrip')}
                    value={mileage.purposeOfTrip || '--'}
                    sx={reviewFieldStyle}
                    invertColor
                  />
                </>
              )}
              <TransactionField
                label={t('reimbursementPage.sheet.notes')}
                value={<span {...sentryMask}>{reimbursement.note || '--'}</span>}
                sx={reviewFieldStyle}
                invertColor
              />
            </Box>
          </Box>

          {/* Project codes */}
          {showCodingSection && codingDisplayValues && (
            <Box>
              <Typography variant="subtitle1" sx={reviewSectionTitleStyle}>
                {t('reimbursementPage.submitForm.projectCodesLabel')}
              </Typography>
              <Box sx={reviewContainerStyle}>
                <MobileCodingFieldList
                  mode="readonly"
                  isVisible={isVisible}
                  getLabel={getLabel}
                  orderedColumnIds={readonlyColumnIds}
                  displayValues={codingDisplayValues}
                  emptyValue="--"
                  fieldSx={reviewFieldStyle}
                  invertColor
                />
              </Box>
            </Box>
          )}

          {/* Documentation */}
          <Box>
            <Typography variant="subtitle1" sx={reviewSectionTitleStyle}>
              {t('reimbursementPage.submitForm.documentationLabel')}
            </Typography>
            <Box sx={reviewContainerStyle}>
              {receiptUrl ? (
                <MobileReceiptPreview viewOnly={true} receiptUrl={receiptUrl} scopeId={selectedPaidolId} />
              ) : receiptError ? (
                <Typography variant="body2" color="error">
                  {t('mobileTransactionDetails.receiptError', 'Failed to load documentation')}
                </Typography>
              ) : (
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                  --
                </Typography>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </MobilePageContent>
  );
};

export default MobileReimbursementDetails;
